import { Spacing } from '@persona-id/design-tokens';
import { TrashIcon } from '@persona-id/design-tokens/icons';
import { groupBy, partition } from 'lodash';
import { uniq } from 'lodash/fp';
import { useEffect } from 'react';
import { FieldErrors } from 'react-hook-form';
import styled from 'styled-components';

import InsightPanelContent from 'dashboard/components/InsightPanel/InsightPanelContent';
import { InsightPanelForm } from 'dashboard/components/InsightPanel/util';
import {
  FieldWriteBackFormatType,
  FieldWriteBackRequest,
  FieldWriteBackSourceType,
  InsightRequest,
  InsightResponseType,
  InsightType,
  MultiSelectInsightOption,
} from 'dashboard/models/document';
import { VerificationTemplateVersionDocument } from 'dashboard/models/verification-template-version';
import Button, { IconButton } from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import { Paragraph } from 'design-system/components/Text';
import { FieldSchema, FieldSchemaType } from 'lib/fields';
import { useFormSecurely } from 'lib/security/reactHookForm';

const PANEL_FOOTER_HEIGHT = 64;

const Form = styled.form`
  flex-direction: column;
  position: relative;
  width: 400px;
  height: 100%;
  overflow: hidden;
`;

const TitleContainer = styled.div`
  padding: ${Spacing[4]};
  padding-bottom: 0;
`;

const BodyContainer = styled.div`
  height: calc(100% - ${PANEL_FOOTER_HEIGHT}px);
  margin-bottom: ${PANEL_FOOTER_HEIGHT}px;
  overflow-y: auto;
`;

const FooterContainer = styled.footer`
  display: flex;
  align-items: center;
  position: absolute;
  bottom: 0px;
  height: ${PANEL_FOOTER_HEIGHT}px;
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.color.border.secondary};
  padding: ${Spacing[5]};
`;

const insightRequestToFormState = (
  insightRequest: InsightRequest,
  fieldWriteBacks: FieldWriteBackRequest[],
): Partial<InsightPanelForm> => {
  let base: Partial<InsightPanelForm> = {
    insightName: insightRequest.label,
    insightKey: insightRequest.key,
    instruction: insightRequest.instruction,
    insightType: insightRequest.type,
  };

  fieldWriteBacks.forEach((fieldWriteBack) => {
    fieldWriteBack.options.forEach((option) => {
      if (
        option['source-type'] === FieldWriteBackSourceType.Insight &&
        option['source-key'] === insightRequest.key
      ) {
        base = {
          ...base,
          saveToDocumentField: true,
          isNewDocumentField: false,
          documentFieldKey: fieldWriteBack['field-name'],
        };
      }
    });
  });

  switch (insightRequest.type) {
    case InsightType.String:
      return base;
    case InsightType.MultiSelect: {
      return {
        ...base,
        responseType: insightRequest['response-type'],
        options: insightRequest.options,
        defaultResponse: insightRequest['default-response'] || '',
      };
    }
  }
};

const renameFieldWriteBacksFieldName = (
  fieldWriteBacks: FieldWriteBackRequest[],
  insightName: string,
  oldFieldName: string | undefined,
  newFieldName: string,
) => {
  if (oldFieldName == null) {
    return fieldWriteBacks;
  }

  const updatedFieldWriteBacks = fieldWriteBacks.flatMap((fw) => {
    if (fw['field-name'] !== oldFieldName) {
      return fw;
    }
    const [extractionOptions, otherOptions] = partition(
      fw.options,
      (o) =>
        o['source-type'] === FieldWriteBackSourceType.Insight && o['source-key'] === insightName,
    );
    const updatedFieldWriteBacks = [];
    if (extractionOptions.length > 0) {
      updatedFieldWriteBacks.push({
        'field-name': newFieldName,
        options: extractionOptions,
      });
    }
    if (otherOptions.length > 0) {
      updatedFieldWriteBacks.push({
        'field-name': oldFieldName,
        options: otherOptions,
      });
    }
    return updatedFieldWriteBacks;
  });

  // regroup them by field name, since we might have created field writebacks with duplicate field name above
  return Object.entries(groupBy(updatedFieldWriteBacks, (fw) => fw['field-name'])).map(
    ([fieldName, groupedFieldWriteBacks]) => ({
      'field-name': fieldName,
      options: groupedFieldWriteBacks.flatMap((fw) => fw.options),
    }),
  );
};

const renameFieldWriteBacksInsightName = (
  fieldWriteBacks: FieldWriteBackRequest[],
  oldInsightName: string | undefined,
  newInsightName: string,
) => {
  if (oldInsightName == null) {
    return fieldWriteBacks;
  }
  return fieldWriteBacks.map((fw) => ({
    ...fw,
    options: fw.options.map((option) =>
      option['source-type'] === FieldWriteBackSourceType.Insight &&
      option['source-key'] === oldInsightName
        ? {
            ...option,
            'source-key': newInsightName,
            formatter: {
              format: {
                ...option.formatter.format,
                template: `{{${newInsightName}}}`,
              },
              'normalization-methods-mappings': option.formatter[
                'normalization-methods-mappings'
              ].map((nm) => {
                return {
                  ...nm,
                  reference: newInsightName,
                };
              }),
            },
            // seunghee update selector when fieldwriteback for v3 is enabled
          }
        : option,
    ),
  }));
};

const filterRequestOutOfFieldWriteBacks = (
  fieldWriteBacks: FieldWriteBackRequest[],
  insightRequest: InsightRequest | null,
) => {
  if (insightRequest == null) {
    return fieldWriteBacks;
  }
  return fieldWriteBacks
    .map((fieldWriteBack) => ({
      ...fieldWriteBack,
      options: fieldWriteBack.options.filter(
        (option) =>
          // Ensure that we retain any other field write back options that are not related to the insight
          option['source-type'] !== FieldWriteBackSourceType.Insight ||
          (option['source-type'] === FieldWriteBackSourceType.Insight &&
            option['source-key'] !== insightRequest.key),
      ),
    }))
    .filter((fieldWriteBack) => fieldWriteBack.options.length > 0);
};

const formStateToRequests = (
  data: InsightPanelForm,
  oldValues: Partial<InsightPanelForm>,
  oldInsightRequest: InsightRequest | null,
  fieldWriteBacks: FieldWriteBackRequest[],
): {
  insightRequest: InsightRequest;
  fieldWriteBackRequests: FieldWriteBackRequest[];
  documentFieldRequest: FieldSchema | null;
} => {
  let insightRequest: InsightRequest;
  let documentFieldRequest: FieldSchema | null = null;

  let documentFieldType: FieldSchemaType;

  switch (data.insightType) {
    case InsightType.String:
      insightRequest = {
        key: data?.insightKey,
        label: data.insightName,
        instruction: data.instruction,
        // NB: model is not exposed on the dashboard, so we use the old model if it exists
        model: oldInsightRequest?.model,
        type: InsightType.String,
      };

      documentFieldType = FieldSchemaType.String;
      break;
    case InsightType.MultiSelect:
      insightRequest = {
        key: data.insightKey,
        label: data.insightName,
        instruction: data.instruction,
        // NB: model is not exposed on the dashboard, so we use the old model if it exists
        model: oldInsightRequest?.model,
        type: InsightType.MultiSelect,
        'response-type': data.responseType,
        options: data.options.map((option) => ({
          value: option.value,
          description: option.description || null,
        })),
        'default-response': null,
      };

      switch (data.responseType) {
        case InsightResponseType.Single:
          documentFieldType = FieldSchemaType.String;
          insightRequest = {
            ...insightRequest,
            'default-response': data.defaultResponse || null,
          };
          break;
        case InsightResponseType.Multiple:
          documentFieldType = FieldSchemaType.Array;
          break;
      }
  }

  if (data.saveToDocumentField) {
    if (
      oldValues.documentFieldKey != null &&
      data.documentFieldKey !== oldValues.documentFieldKey
    ) {
      fieldWriteBacks = renameFieldWriteBacksFieldName(
        fieldWriteBacks,
        insightRequest.key,
        oldValues.documentFieldKey,
        data.documentFieldKey,
      );
    }
    if (oldValues.insightKey !== data.insightKey) {
      fieldWriteBacks = renameFieldWriteBacksInsightName(
        fieldWriteBacks,
        oldValues.insightKey,
        data.insightKey,
      );
    }

    let option = fieldWriteBacks
      .find((fieldWriteBack) => fieldWriteBack['field-name'] === data.documentFieldKey)
      ?.options?.find(
        (option) =>
          option['source-type'] === FieldWriteBackSourceType.Insight &&
          option['source-key'] === insightRequest?.key,
      );

    if (option == null) {
      switch (documentFieldType) {
        case FieldSchemaType.String:
          option = {
            'extraction-name': data.insightKey,
            'source-key': data.insightKey,
            'source-type': FieldWriteBackSourceType.Insight,
            formatter: {
              format: {
                type: FieldWriteBackFormatType.TemplatedString,
                template: `{{${data.insightKey}}}`,
              },
              'normalization-methods-mappings': [],
            },
          };
          break;
        case FieldSchemaType.Array:
          option = {
            'extraction-name': data.insightKey,
            'source-key': data.insightKey,
            'source-type': FieldWriteBackSourceType.Insight,
            formatter: {
              format: {
                type: FieldWriteBackFormatType.Array,
                reference: data.insightKey,
              },
              'normalization-methods-mappings': [],
            },
          };
          break;
      }
      const fieldWriteBackIndex = fieldWriteBacks.findIndex(
        (fieldWriteBack) => fieldWriteBack['field-name'] === data.documentFieldKey,
      );
      let options = fieldWriteBacks[fieldWriteBackIndex]?.options || [];
      const optionIndex = options.findIndex(
        (option) =>
          option['source-type'] === FieldWriteBackSourceType.Insight &&
          option['source-key'] === oldInsightRequest?.key,
      );
      if (optionIndex === -1) {
        options = [...options, option];
      } else {
        options = [...options.slice(0, optionIndex), option, ...options.slice(optionIndex + 1)];
      }
      const fieldWriteBack = {
        'field-name': data.documentFieldKey,
        options: options,
      };

      if (fieldWriteBackIndex === -1) {
        fieldWriteBacks = [...fieldWriteBacks, fieldWriteBack];
      } else {
        fieldWriteBacks = [
          ...fieldWriteBacks.slice(0, fieldWriteBackIndex),
          fieldWriteBack,
          ...fieldWriteBacks.slice(fieldWriteBackIndex + 1),
        ];
      }
    }

    if (data.isNewDocumentField) {
      switch (documentFieldType) {
        case FieldSchemaType.String:
          documentFieldRequest = {
            key: data.documentFieldKey,
            label: data.documentFieldName,
            type: documentFieldType,
            config: {},
          };
          break;
        case FieldSchemaType.Array:
          documentFieldRequest = {
            key: data.documentFieldKey,
            label: data.documentFieldName,
            type: documentFieldType,
            config: {
              'item-schema': {
                // NB: allows back-end to define default
                key: null as never,
                label: null as never,
                type: FieldSchemaType.String,
                config: {},
              },
            },
          };
          break;
      }
    }
  } else {
    fieldWriteBacks = filterRequestOutOfFieldWriteBacks(fieldWriteBacks, oldInsightRequest);
  }

  return { insightRequest, fieldWriteBackRequests: fieldWriteBacks, documentFieldRequest };
};

const errorRequired = { type: 'required', message: 'Required' };
const insightKeyRegex = /^[a-z0-9]+(_[a-z0-9]+)*$/;

const validateInsightPanelForm = (
  data: InsightPanelForm,
  insights: InsightRequest[],
  insightIndex: number | null,
  documentFields: FieldSchema[],
) => {
  const errors: FieldErrors<InsightPanelForm> = {};

  const usedInsightNames = insights
    .map((insight) => insight.label)
    .filter((_, index) => index !== insightIndex);
  const usedInsightKeys = insights
    .map((insight) => insight.key)
    .filter((_, index) => index !== insightIndex);
  const usedDocumentFieldNames = documentFields.map((documentField) => documentField.label);
  const usedDocumentFieldKeys = documentFields.map((documentField) => documentField.key);

  if (data?.insightName == null || data?.insightName === '') {
    errors.insightName = errorRequired;
  } else if (usedInsightNames.includes(data.insightName)) {
    errors.insightName = {
      type: 'conflict',
      message: 'An insight with this label already exists. Please choose a different label.',
    };
  }
  if (data?.insightKey == null || data?.insightKey === '') {
    errors.insightKey = errorRequired;
  } else if (!data?.insightKey.match(insightKeyRegex)) {
    errors.insightKey = {
      type: 'pattern',
      message: 'Insight key must be in snake case (e.g. my_insight_key)',
    };
  } else if (usedInsightKeys.includes(data.insightKey)) {
    errors.insightKey = {
      type: 'conflict',
      message: 'An insight with this key already exists. Please choose a different key.',
    };
  }
  if (data?.insightType == null) {
    errors.insightType = errorRequired;
  }
  if (data?.instruction == null || data?.instruction === '') {
    errors.instruction = errorRequired;
  } else if (data.instruction.length > 500) {
    errors.instruction = {
      type: 'maxLength',
      message: 'Maximum of 500 characters',
    };
  }
  switch (data?.insightType) {
    case InsightType.String:
      break;
    case InsightType.MultiSelect:
      if (data?.responseType == null) {
        errors.responseType = errorRequired;
      }
      if (data?.options == null || data.options.length === 0) {
        errors.options = {
          type: 'required',
          message: 'At least one option is required',
        };
      } else if (data.options.length !== uniq(data.options.map((option) => option.value)).length) {
        errors.options = {
          type: 'unique',
          message: 'Option values must be unique',
        };
      } else {
        const optionErrors = data.options.map((option) => {
          const optionError: {
            [K in keyof MultiSelectInsightOption]?: { type: string; message: string };
          } = {};
          if (option.value == null || option.value === '') {
            optionError.value = errorRequired;
          } else if (option.value.length > 50) {
            optionError.value = {
              type: 'maxLength',
              message: 'Maximum of 50 characters',
            };
          }

          if (option.description != null && option.description.length > 250) {
            optionError.description = {
              type: 'maxLength',
              message: 'Maximum of 250 characters',
            };
          }

          if (Object.keys(optionError).length > 0) {
            return optionError;
          }
        });

        if (optionErrors.some((option) => option)) {
          errors.options = optionErrors;
        }
      }
      break;
  }
  if (data?.saveToDocumentField) {
    if (data?.isNewDocumentField) {
      if (data?.documentFieldName == null || data?.documentFieldName === '') {
        errors.documentFieldName = errorRequired;
      } else if (usedDocumentFieldNames.includes(data.documentFieldName)) {
        errors.documentFieldName = {
          type: 'conflict',
          message:
            'A document field with this label already exists. Please choose a different label.',
        };
      }
      if (usedDocumentFieldKeys.includes(data.documentFieldKey)) {
        errors.documentFieldKey = {
          type: 'conflict',
          message: 'A document field with this key already exists. Please choose a different key.',
        };
      }
    } /* not a new field */ else if (
      data?.documentFieldKey == null ||
      data?.documentFieldKey === ''
    ) {
      errors.documentFieldKey = errorRequired;
    }
  }

  return Object.keys(errors).length === 0
    ? { values: data, errors: {} }
    : { values: {}, errors: errors };
};

interface InsightPanelProps {
  vtmplVersion: VerificationTemplateVersionDocument;
  // index of currently selected insight (null if new insight)
  insightIndex: number | null;
  closeInsight: () => void;
  addInsight: (
    insightRequest: InsightRequest,
    fieldWriteBackRequests: FieldWriteBackRequest[],
    documentFieldRequest: FieldSchema | null,
  ) => void;
  updateInsight: (
    insightRequest: InsightRequest,
    fieldWriteBackRequests: FieldWriteBackRequest[],
    documentFieldRequest: FieldSchema | null,
  ) => void;
  deleteInsight: () => void;
}

const InsightPanel = ({
  vtmplVersion,
  insightIndex,
  closeInsight,
  addInsight,
  updateInsight,
  deleteInsight,
}: InsightPanelProps) => {
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        closeInsight();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [closeInsight]);

  const isNew = insightIndex == null;
  let defaultValues: Partial<InsightPanelForm> = {
    responseType: InsightResponseType.Single,
    saveToDocumentField: false,
    isNewDocumentField: false,
  };

  const insights = vtmplVersion.attributes.insights;
  const documentFields = vtmplVersion.attributes['field-schemas'] || [];
  const fieldWriteBacks = vtmplVersion.attributes['field-write-backs'] || [];

  if (!isNew) {
    const insightRequest = insights[insightIndex];

    defaultValues = {
      ...defaultValues,
      ...insightRequestToFormState(insightRequest, fieldWriteBacks),
    };
  }

  const formReturn = useFormSecurely<InsightPanelForm>({
    defaultValues,
    resolver: (data) => validateInsightPanelForm(data, insights, insightIndex, documentFields),
    mode: 'onChange',
  });
  const { watch, handleSubmit } = formReturn;

  const isNewDocumentField = watch('isNewDocumentField');

  const handleSubmitForm = handleSubmit((data) => {
    const { insightRequest, fieldWriteBackRequests, documentFieldRequest } = formStateToRequests(
      data,
      defaultValues,
      isNew ? null : insights[insightIndex],
      fieldWriteBacks,
    );
    if (isNew) {
      addInsight(insightRequest, fieldWriteBackRequests, documentFieldRequest);
    } else {
      updateInsight(insightRequest, fieldWriteBackRequests, documentFieldRequest);
    }
    closeInsight();
  });

  const handleCancel = () => {
    closeInsight();
  };

  const handleDelete = () => {
    deleteInsight();
    closeInsight();
  };

  return (
    <Form id='insight-panel__form' onSubmit={handleSubmitForm}>
      <BodyContainer>
        <TitleContainer>
          <Paragraph size='lg' weight='bold' color='primary'>
            Automate {isNew && 'new'} insight
          </Paragraph>
          AI can automatically analyze the document to generate relevant insights.
        </TitleContainer>
        <InsightPanelContent formReturn={formReturn} vtmplVersion={vtmplVersion} />
      </BodyContainer>
      <FooterContainer>
        <HStack style={{ flexGrow: 1 }}>
          <Button
            id={`insight-panel__${isNew ? 'add' : 'update'}-button`}
            type='submit'
            width='auto'
            onClick={handleSubmitForm}
          >
            {isNew ? 'Add' : 'Update'}
            {isNewDocumentField && ' & Create New Field'}
          </Button>
          <Button
            id='insight-panel__close-button'
            width='auto'
            variant='basic'
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </HStack>
        <IconButton id='insight-panel__delete-button' onClick={handleDelete} disabled={isNew}>
          <TrashIcon />
        </IconButton>
      </FooterContainer>
    </Form>
  );
};

export default InsightPanel;
